/**
 * WHERE THE REPORT'S PHOTOGRAPHS GO.
 *
 * Tom, 27 Sep: the wrap-up pictures are "just to make the report look
 * pretty... it wouldn't be all the photos for download". So they are not the
 * gallery. The gallery is a hand-off, ninety-seven frames, take what you
 * want. These are a handful the studio chose to carry the report, and they
 * behave the way the figures do: the studio decides the content, the page
 * decides the arrangement. Staff upload them in an order; nobody is asked
 * where each one should sit.
 *
 * THE SEAMS. A report has natural places where a band ends and the eye needs
 * somewhere to rest. There are five, and a seam only exists if the section it
 * belongs to exists -- the same rule the section numbering already follows,
 * so a report with no testimonials never leaves a picture above nothing.
 *
 *   opening      under the statement, before the first figures
 *   afterFilms   after the films, before the middle figures
 *   closing      inside the ink block, beside the closing paragraph
 *   voices       before "What they said"
 *   next         before "Where this could go"
 *
 * EIGHT IS THE CAP. Past eight a report becomes a contact sheet and stops
 * being a report. The editor says so rather than silently dropping them.
 *
 * ORIENTATION DECIDES THE SHAPE, NOT THE ORDER. A wide seam takes a landscape
 * frame if one is left; if not it takes two photographs side by side instead.
 * So a portrait is never stretched across the page, and no photograph is ever
 * stranded unplaced -- which is the failure this would otherwise have, and
 * the same one the twelve-figure band had before the remainder rule.
 *
 * The ink block is the one seam that actively wants a portrait: it is the
 * darkest, emptiest moment on the page and a standing figure beside that
 * paragraph is the best thing in the report.
 */

export interface WrapUpPhoto {
  /** Storage path, so it can be removed when staff take it out. */
  path: string
  /** Where the browser loads it from. */
  url: string
  /** Of the stored (resized) image. Aspect is what matters, not size. */
  width: number
  height: number
}

/** Past this a report is a contact sheet. */
export const MAX_REPORT_PHOTOS = 8

export type PhotoSeam = 'opening' | 'afterFilms' | 'closing' | 'voices' | 'next'

/**
 * plate  one landscape, full width
 * pair   two side by side, square cells
 * trio   three across, square cells
 * inset  one beside the closing paragraph, inside the ink
 */
export type PhotoShape = 'plate' | 'pair' | 'trio' | 'inset'

export interface Placement {
  seam: PhotoSeam
  shape: PhotoShape
  photos: WrapUpPhoto[]
}

/** Which sections this particular report actually has. */
export interface ReportSections {
  closing: boolean
  voices: boolean
  next: boolean
}

function isLandscape(photo: WrapUpPhoto): boolean {
  /* A square reads as landscape: it fills a wide slot without being cropped
     to a letterbox, which is the thing this test is actually for. */
  return photo.width >= photo.height
}

/** jsonb arrives as whatever was stored, so every row is checked. */
export function photosOf(value: unknown): WrapUpPhoto[] {
  if (!Array.isArray(value)) return []
  return value
    .map((row) => {
      const record = (row ?? {}) as Record<string, unknown>
      return {
        path: String(record.path ?? ''),
        url: String(record.url ?? ''),
        width: Number(record.width) || 0,
        height: Number(record.height) || 0,
      }
    })
    .filter((photo) => photo.url !== '')
}

/**
 * The arrangement, worked out from the photographs and the sections this
 * report happens to have. Pure: same input, same page, every time.
 */
export function placePhotos(
  photos: WrapUpPhoto[],
  sections: ReportSections,
): Placement[] {
  const pool = photos.slice(0, MAX_REPORT_PHOTOS)
  if (pool.length === 0) return []

  /** Removes and returns the first photo matching `want`, else the first. */
  function take(want?: 'landscape' | 'portrait'): WrapUpPhoto | undefined {
    if (pool.length === 0) return undefined
    let at = 0
    if (want) {
      const found = pool.findIndex((photo) =>
        want === 'landscape' ? isLandscape(photo) : !isLandscape(photo),
      )
      if (found >= 0) at = found
    }
    return pool.splice(at, 1)[0]
  }

  const placements: Placement[] = []

  /**
   * A full-width seam. A landscape fills it alone; with none left, two
   * photographs share it rather than one portrait being stretched across the
   * page. One portrait and nothing else is the only case that crops, and a
   * wide crop of a decorative frame is a reasonable last resort.
   */
  function wide(seam: PhotoSeam) {
    if (pool.length === 0) return
    if (pool.some(isLandscape)) {
      placements.push({ seam, shape: 'plate', photos: [take('landscape')!] })
      return
    }
    if (pool.length >= 2) {
      placements.push({ seam, shape: 'pair', photos: [take()!, take()!] })
      return
    }
    placements.push({ seam, shape: 'plate', photos: [take()!] })
  }

  function some(seam: PhotoSeam, shape: PhotoShape, count: number) {
    const chosen: WrapUpPhoto[] = []
    while (chosen.length < count && pool.length > 0) chosen.push(take()!)
    if (chosen.length === 0) return
    /* Two out of a row of three is still a pair, not a gappy trio. */
    const actual: PhotoShape =
      shape === 'trio' && chosen.length < 3 ? (chosen.length === 2 ? 'pair' : 'plate') : shape
    placements.push({ seam, shape: actual, photos: chosen })
  }

  wide('opening')
  some('afterFilms', 'pair', 2)
  if (sections.closing && pool.length > 0) {
    placements.push({ seam: 'closing', shape: 'inset', photos: [take('portrait')!] })
  }
  if (sections.voices) some('voices', 'trio', 3)
  if (sections.next) wide('next')

  return placements
}

/** The placement for one seam, or undefined. Read by the page. */
export function atSeam(placements: Placement[], seam: PhotoSeam): Placement | undefined {
  return placements.find((placement) => placement.seam === seam)
}

/**
 * What the editor tells staff, in words, about what will happen to what they
 * have uploaded. Never a silent truncation.
 */
export function placementSummary(count: number, sections: ReportSections): string {
  if (count === 0) return 'No photographs. The report runs as text and figures alone.'
  const placed = Math.min(count, MAX_REPORT_PHOTOS)
  const seams = placePhotos(
    Array.from({ length: placed }, (_, index) => ({
      path: '',
      url: `x${index}`,
      width: 3,
      height: 2,
    })),
    sections,
  ).length
  const spare = count - placed
  const first = `${placed} ${placed === 1 ? 'photograph' : 'photographs'} placed across ${seams} ${seams === 1 ? 'point' : 'points'} in the report.`
  if (spare === 0) return first
  return `${first} The other ${spare} will not be shown — eight is the most a report can carry before it reads as a contact sheet.`
}
